import type { EpisodeDto } from '../../shared/types';
import {
  cancelDownload,
  downloadOf,
  hasNative,
  removeDownload,
  startDownload,
} from '../state/downloads';
import { INSECURE_ENCLOSURE_ERROR, INSECURE_ENCLOSURE_MESSAGE } from '../state/native';
import { Icon } from './Icon';

interface Props {
  episode: EpisodeDto;
  /** Renders the wider labelled form used on the episode page. */
  variant?: 'icon' | 'button';
}

type Mode = 'idle' | 'active' | 'complete' | 'failed' | 'insecure';

/** One table rather than five parallel ternary chains over the same five states. */
const MODES = {
  idle: { icon: 'download', text: 'Download', verb: 'Download' },
  active: { icon: 'x', text: 'Downloading…', verb: 'Cancel download of' },
  complete: { icon: 'downloaded', text: 'Downloaded', verb: 'Delete downloaded' },
  failed: { icon: 'warning', text: 'Retry download', verb: 'Retry download of' },
  // Retrying this one is never going to succeed — the publisher's feed, not the network, is the
  // problem — so it gets its own label rather than "Retry download" over the same explanation.
  insecure: { icon: 'warning', text: 'Insecure connection', verb: 'Insecure download of' },
} as const;

/**
 * Download control for the Android shell.
 *
 * Renders nothing in a browser: downloads are a native capability, and an affordance that cannot
 * work is worse than none. All the state lives in `state/downloads.ts`.
 */
export function DownloadButton({ episode, variant = 'icon' }: Props) {
  if (!hasNative.value) return null;

  const row = downloadOf(episode.id);
  const mode: Mode =
    row?.status === 'QUEUED' || row?.status === 'RUNNING'
      ? 'active'
      : row?.status === 'COMPLETE'
        ? 'complete'
        : row?.status === 'FAILED'
          ? row.error === INSECURE_ENCLOSURE_ERROR
            ? 'insecure'
            : 'failed'
          : 'idle';

  const { icon, text, verb } = MODES[mode];
  const label = `${verb} ${episode.title}`;
  const tooltip =
    mode === 'insecure'
      ? INSECURE_ENCLOSURE_MESSAGE
      : row?.error
        ? `Download failed (${row.error})`
        : label;
  const percent =
    row && row.bytesTotal > 0
      ? Math.min(100, Math.round((row.bytesDone / row.bytesTotal) * 100))
      : 0;

  const onClick = () => {
    if (mode === 'active') void cancelDownload(episode.id);
    else if (mode === 'complete') void removeDownload(episode.id);
    else void startDownload(episode);
  };

  if (variant === 'button') {
    return (
      <button
        type="button"
        class="button button--ghost"
        onClick={onClick}
        aria-label={label}
        title={tooltip}
      >
        <Icon name={icon} size={18} />
        <span>{mode === 'active' && row?.bytesTotal ? `Downloading ${percent}%` : text}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      class={`icon-button download-button ${mode === 'complete' ? 'is-complete' : ''}`}
      onClick={onClick}
      aria-label={label}
      title={tooltip}
    >
      {mode === 'active' && (
        <span
          class="download-button__ring"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label={`Download progress for ${episode.title}`}
          style={{ '--download-progress': `${percent}%` } as Record<string, string>}
        />
      )}
      <Icon name={icon} size={20} />
    </button>
  );
}
